import React from 'react';
import { Card, CardContent } from '@/components/ui/card';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Loader2, ClipboardCheck, Send, Save, Check } from 'lucide-react';
import { portalQuestionnaire } from '@/lib/api';

const PORTAL_TOKEN_KEY = 'immipro_portal_token';
const PORTAL_MATTER_KEY = 'immipro_portal_matter';

export default function PortalQuestionnairePage() {
  const [token] = React.useState(() => localStorage.getItem(PORTAL_TOKEN_KEY));
  const [matterRef] = React.useState(() => localStorage.getItem(PORTAL_MATTER_KEY));
  const [schema, setSchema] = React.useState(null);
  const [answers, setAnswers] = React.useState({});
  const [submitted, setSubmitted] = React.useState(false);
  const [savedAt, setSavedAt] = React.useState(null);
  const [saving, setSaving] = React.useState(false);
  const [submitting, setSubmitting] = React.useState(false);
  const [activeSection, setActiveSection] = React.useState(0);
  const [error, setError] = React.useState(null);

  React.useEffect(() => {
    if (!token || !matterRef) {
      setError('Please sign in via your magic link email first.');
      return;
    }
    portalQuestionnaire.get(matterRef, token)
      .then((d) => {
        setSchema(d);
        setAnswers(d.answers || {});
        setSubmitted(d.submitted);
        setSavedAt(d.updated_at);
      })
      .catch((e) => setError(e?.response?.data?.detail || e.message));
  }, [token, matterRef]);

  const patch = (key, value) => setAnswers((a) => ({ ...a, [key]: value }));

  const save = async (submitFinal = false) => {
    if (submitFinal) setSubmitting(true); else setSaving(true);
    try {
      const d = await portalQuestionnaire.save(matterRef, answers, submitFinal, token);
      setSubmitted(d.submitted);
      setSavedAt(d.updated_at);
    } catch (e) {
      setError(e?.response?.data?.detail || e.message);
    } finally { setSaving(false); setSubmitting(false); }
  };

  const totalFields = React.useMemo(() =>
    (schema?.sections || []).reduce((sum, s) => sum + (s.fields?.length || 0), 0), [schema]);
  const filledCount = React.useMemo(() =>
    (schema?.sections || []).reduce((sum, s) =>
      sum + s.fields.filter((f) => answers[f.key] !== undefined && answers[f.key] !== '').length, 0), [schema, answers]);
  const progress = totalFields > 0 ? Math.round((filledCount / totalFields) * 100) : 0;

  if (error) {
    return (
      <div className="mx-auto max-w-2xl p-8">
        <Card><CardContent className="p-6 text-sm text-red-600">{error}</CardContent></Card>
      </div>
    );
  }
  if (!schema) {
    return (
      <div className="mx-auto max-w-4xl p-8">
        <Loader2 className="h-6 w-6 animate-spin text-[#2ECC9A]" />
      </div>
    );
  }

  const currentSection = schema.sections[activeSection] || schema.sections[0];

  return (
    <div className="min-h-screen bg-slate-50">
      <div className="mx-auto max-w-4xl p-6 lg:p-10" data-testid="portal-questionnaire">
        <div className="flex items-center gap-3">
          <ClipboardCheck className="h-7 w-7 text-[#2ECC9A]" />
          <div>
            <h1 className="text-2xl font-serif font-bold text-[#1A3A5C]">{schema.title}</h1>
            <div className="text-xs text-slate-500">
              Matter <span className="font-mono">{matterRef}</span>
              {submitted && <Badge className="ml-2 bg-emerald-100 text-emerald-700 border-emerald-200 text-[10px]"><Check className="mr-1 h-3 w-3"/> Submitted</Badge>}
              {savedAt && !submitted && <span className="ml-2 text-slate-400">Last saved {new Date(savedAt).toLocaleString('en-AU')}</span>}
            </div>
          </div>
        </div>

        <div className="mt-4 rounded-md bg-white border border-slate-200 p-3">
          <div className="flex items-center justify-between text-xs text-slate-500">
            <span>{filledCount} of {totalFields} fields complete</span>
            <span className="font-medium text-[#1A3A5C]">{progress}%</span>
          </div>
          <div className="mt-1 h-2 rounded-full bg-slate-100 overflow-hidden">
            <div className="h-full bg-[#2ECC9A] transition-all" style={{ width: `${progress}%` }} />
          </div>
        </div>

        {/* Section tabs */}
        <div className="mt-4 flex flex-wrap gap-1 rounded-md border border-slate-200 bg-white p-1">
          {schema.sections.map((s, i) => (
            <button key={s.key} data-testid={`section-tab-${s.key}`}
                    onClick={() => setActiveSection(i)}
                    className={`rounded px-3 py-1.5 text-xs font-medium ${
                      i === activeSection ? 'bg-[#1A3A5C] text-white' : 'text-slate-600 hover:bg-slate-50'
                    }`}>
              {i + 1}. {s.label}
            </button>
          ))}
        </div>

        <Card className="mt-4 border-slate-200">
          <CardContent className="p-6">
            <div className="text-sm font-semibold text-[#1A3A5C]">{currentSection.label}</div>
            <div className="mt-4 grid gap-4 md:grid-cols-2">
              {currentSection.fields.map((f) => (
                <QField key={f.key} field={f} value={answers[f.key]} onChange={(v) => patch(f.key, v)} disabled={submitted} />
              ))}
            </div>

            <div className="mt-6 flex items-center justify-between gap-2">
              <Button variant="outline" disabled={activeSection === 0}
                      onClick={() => setActiveSection((i) => Math.max(0, i - 1))}
                      data-testid="portal-q-prev" className="h-9 text-xs">← Back</Button>
              <div className="flex items-center gap-2">
                <Button data-testid="portal-q-save" variant="outline" onClick={() => save(false)}
                        disabled={saving || submitting || submitted}
                        className="h-9 gap-1 text-xs">
                  {saving ? <Loader2 className="h-3 w-3 animate-spin" /> : <><Save className="h-3 w-3"/> Save draft</>}
                </Button>
                {activeSection < schema.sections.length - 1 ? (
                  <Button data-testid="portal-q-next"
                          onClick={() => setActiveSection((i) => Math.min(schema.sections.length - 1, i + 1))}
                          className="h-9 bg-[#1A3A5C] text-white hover:bg-[#1A3A5C]/90 text-xs">
                    Next →
                  </Button>
                ) : (
                  <Button data-testid="portal-q-submit" onClick={() => save(true)}
                          disabled={submitting || submitted}
                          className="h-9 gap-1 bg-[#2ECC9A] text-[#0F1F3A] hover:bg-[#2ECC9A]/90 text-xs">
                    {submitting ? <Loader2 className="h-3 w-3 animate-spin" /> : <><Send className="h-3 w-3"/> Submit questionnaire</>}
                  </Button>
                )}
              </div>
            </div>
          </CardContent>
        </Card>

        <div className="mt-4 text-center text-[11px] text-slate-500">
          Your migration adviser will be notified when you submit.
          You can save progress at any time and return via the link in your email.
        </div>
      </div>
    </div>
  );
}

function QField({ field, value, onChange, disabled }) {
  const testId = `field-${field.key}`;
  if (field.type === 'textarea') {
    return (
      <div className="md:col-span-2">
        <Label className="text-xs">{field.label}{field.required && <span className="text-red-500"> *</span>}</Label>
        <textarea data-testid={testId} value={value || ''} disabled={disabled}
                  onChange={(e) => onChange(e.target.value)} rows={3}
                  className="mt-1 w-full rounded-md border border-slate-200 bg-white p-2 text-sm disabled:bg-slate-50" />
      </div>
    );
  }
  if (field.type === 'select') {
    return (
      <div>
        <Label className="text-xs">{field.label}{field.required && <span className="text-red-500"> *</span>}</Label>
        <select data-testid={testId} value={value || ''} disabled={disabled}
                onChange={(e) => onChange(e.target.value)}
                className="mt-1 h-10 w-full rounded-md border border-slate-200 bg-white px-2 text-sm disabled:bg-slate-50">
          <option value="">— select —</option>
          {(field.options || []).map((o) => <option key={o} value={o}>{o}</option>)}
        </select>
      </div>
    );
  }
  return (
    <div>
      <Label className="text-xs">{field.label}{field.required && <span className="text-red-500"> *</span>}</Label>
      <Input data-testid={testId} value={value || ''} disabled={disabled}
             onChange={(e) => onChange(e.target.value)}
             type={field.type === 'date' ? 'date' : field.type === 'number' ? 'number' : 'text'}
             className="mt-1 text-sm disabled:bg-slate-50" />
    </div>
  );
}
